import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo, useState } from 'react'
import type {
  PaymentScheduleEntry,
  PaymentType as PaymentTypeEnum,
  Project,
} from '../../types/project'
import CurrencyInput from '../CurrencyInput'
import Select from '../Select'

interface PaymentInfoStepProps {
  onUpdate: (data: Partial<Project>) => void
  data: Partial<Project>
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
}

const PAYMENT_TYPE_OPTIONS = [
  { value: 'RATE_TYPE', label: 'Rate Type' },
  { value: 'MILESTONE', label: 'Milestone Payment' },
  { value: 'PROGRESS', label: 'Progress Payment' },
]

const RATE_TYPE_OPTIONS = [
  { value: 'LOAD', label: 'Load' },
  { value: 'WEIGHT', label: 'Weight' },
  { value: 'TIME', label: 'Time' },
]

const createEmptyEntry = (): PaymentScheduleEntry => ({
  deliverable: '',
  due_date: '',
  pay_amount: '',
  completed: false,
})

const PaymentInfoStep: React.FC<PaymentInfoStepProps> = ({
  onUpdate,
  data,
  readOnly = false,
  onValidationChange,
  showErrors = false,
}) => {
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(
    data.payment_type || 'RATE_TYPE',
  )
  const [scheduleEntries, setScheduleEntries] = useState<
    PaymentScheduleEntry[]
  >(
    data.payment_schedule && data.payment_schedule.length > 0
      ? data.payment_schedule
      : [createEmptyEntry()],
  )

  // Validation logic: Payment Type required, conditional validation based on type
  const isValid = useMemo(() => {
    // Payment type must be selected
    if (!selectedPaymentType) return false

    if (selectedPaymentType === 'RATE_TYPE') {
      // For Rate Type: Rate Type and Pay Rate are required
      const hasRateType = Boolean(
        data.rate_type && data.rate_type.trim() !== '',
      )
      const hasPayRate = Boolean(
        data.pay_rate &&
          data.pay_rate.toString().trim() !== '' &&
          Number.parseFloat(data.pay_rate) > 0,
      )
      return hasRateType && hasPayRate
    }
    // For Milestone/Progress: At least one schedule entry with a pay amount is required
    const hasValidEntry = scheduleEntries.some(
      (entry) =>
        entry.pay_amount &&
        entry.pay_amount.toString().trim() !== '' &&
        Number.parseFloat(entry.pay_amount.toString()) > 0,
    )
    return hasValidEntry
  }, [selectedPaymentType, data.rate_type, data.pay_rate, scheduleEntries])

  // Report validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [isValid, onValidationChange])

  // Check for individual field errors
  const hasPaymentTypeError = showErrors && !selectedPaymentType
  const hasRateTypeError =
    showErrors &&
    selectedPaymentType === 'RATE_TYPE' &&
    (!data.rate_type || data.rate_type.trim() === '')
  const hasPayRateError =
    showErrors &&
    selectedPaymentType === 'RATE_TYPE' &&
    (!data.pay_rate ||
      data.pay_rate.toString().trim() === '' ||
      Number.parseFloat(data.pay_rate) <= 0)
  const hasScheduleError =
    showErrors &&
    (selectedPaymentType === 'MILESTONE' ||
      selectedPaymentType === 'PROGRESS') &&
    !scheduleEntries.some(
      (entry) =>
        entry.pay_amount &&
        entry.pay_amount.toString().trim() !== '' &&
        Number.parseFloat(entry.pay_amount.toString()) > 0,
    )

  // Calculate totals whenever entries change
  const calculateTotals = (entries: PaymentScheduleEntry[]) => {
    let totalPay = 0

    for (const entry of entries) {
      const payAmount =
        typeof entry.pay_amount === 'string'
          ? Number.parseFloat(entry.pay_amount) || 0
          : entry.pay_amount || 0
      totalPay += payAmount
    }

    return { totalPay }
  }

  // Handle payment type change
  const handlePaymentTypeChange = (newType: string) => {
    setSelectedPaymentType(newType)

    if (newType === 'RATE_TYPE') {
      onUpdate({
        payment_type: newType as PaymentTypeEnum,
        payment_schedule: [],
        total_scheduled_payment: 0,
      })
    } else {
      // Reset entries when switching to a schedule-based type
      const newEntries =
        selectedPaymentType === 'RATE_TYPE'
          ? [createEmptyEntry()]
          : scheduleEntries
      if (selectedPaymentType === 'RATE_TYPE') {
        setScheduleEntries(newEntries)
      }
      const { totalPay } = calculateTotals(newEntries)
      onUpdate({
        payment_type: newType as PaymentTypeEnum,
        payment_schedule: newEntries,
        total_scheduled_payment: totalPay,
      })
    }
  }

  // Update parent when schedule entries change
  const updateScheduleEntries = (newEntries: PaymentScheduleEntry[]) => {
    setScheduleEntries(newEntries)
    const { totalPay } = calculateTotals(newEntries)
    onUpdate({
      payment_type: selectedPaymentType as PaymentTypeEnum,
      payment_schedule: newEntries,
      total_scheduled_payment: totalPay,
    })
  }

  const handleEntryChange = (
    index: number,
    field: keyof PaymentScheduleEntry,
    value: string | boolean,
  ) => {
    const newEntries = [...scheduleEntries]
    newEntries[index] = {
      ...newEntries[index],
      [field]: value,
    }
    updateScheduleEntries(newEntries)
  }

  const addEntry = () => {
    updateScheduleEntries([...scheduleEntries, createEmptyEntry()])
  }

  const removeEntry = (index: number) => {
    if (scheduleEntries.length > 1) {
      const newEntries = scheduleEntries.filter((_, i) => i !== index)
      updateScheduleEntries(newEntries)
    }
  }

  const { totalPay } = calculateTotals(scheduleEntries)

  const renderScheduleForm = (buttonLabel: string) => (
    <div className={css(styles.paymentSection)}>
      {scheduleEntries.map((entry, index) => (
        <div
          key={`entry-${entry.deliverable}-${index}`}
          className={css(styles.entryContainer)}
        >
          <div className={css(styles.entryHeader)}>
            <span className={css(styles.entryNumber)}>Entry {index + 1}</span>
            {!readOnly && scheduleEntries.length > 1 && (
              <button
                type="button"
                className={css(styles.removeButton)}
                onClick={() => removeEntry(index)}
              >
                <i className="fa-solid fa-trash" />
              </button>
            )}
          </div>
          <div className={css(styles.formGroup)}>
            <label className={css(styles.label)}>Deliverable(s)</label>
            <input
              disabled={readOnly}
              type="text"
              value={entry.deliverable}
              onChange={(e) =>
                handleEntryChange(index, 'deliverable', e.target.value)
              }
              placeholder="Enter deliverable description"
              className={css(styles.input)}
            />
          </div>
          <div className={css(styles.row)}>
            <div className={css(styles.formGroup, styles.halfWidth)}>
              <label className={css(styles.label)}>Due Date</label>
              <input
                disabled={readOnly}
                type="date"
                value={entry.due_date}
                onChange={(e) =>
                  handleEntryChange(index, 'due_date', e.target.value)
                }
                className={css(styles.input)}
              />
            </div>
            <div className={css(styles.formGroup, styles.halfWidth)}>
              <label className={css(styles.label)}>Pay Amount</label>
              <CurrencyInput
                value={entry.pay_amount}
                onChange={(value) =>
                  handleEntryChange(index, 'pay_amount', value)
                }
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      ))}

      {!readOnly && (
        <button
          type="button"
          className={css(styles.addButton)}
          onClick={addEntry}
        >
          <i className="fa-solid fa-plus" /> {buttonLabel}
        </button>
      )}

      {/* Totals Display */}
      <div className={css(styles.totalsSection)}>
        <div className={css(styles.totalRow, styles.grandTotal)}>
          <span className={css(styles.totalLabel)}>
            Total Scheduled Payment:
          </span>
          <span className={css(styles.totalValue)}>${totalPay.toFixed(2)}</span>
        </div>
      </div>
    </div>
  )

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>Payment Information</h3>
      <p className={css(styles.requiredNote)}>* Required field</p>
      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Payment Type *</label>
        <div className={css(hasPaymentTypeError && styles.errorHighlight)}>
          <Select
            isDisabled={readOnly}
            placeholder="Select Payment Type"
            options={PAYMENT_TYPE_OPTIONS}
            value={PAYMENT_TYPE_OPTIONS.find(
              (opt) => opt.value === selectedPaymentType,
            )}
            onChange={(option) =>
              handlePaymentTypeChange(option?.value || 'RATE_TYPE')
            }
          />
        </div>
        {hasPaymentTypeError && (
          <div className={css(styles.errorMessage)}>
            Please select a payment type.
          </div>
        )}
      </div>

      {selectedPaymentType === 'RATE_TYPE' && (
        <div className={css(styles.row)}>
          <div className={css(styles.formGroup, styles.halfWidth)}>
            <label className={css(styles.label)}>Rate Type *</label>
            <div className={css(hasRateTypeError && styles.errorHighlight)}>
              <Select
                isDisabled={readOnly}
                placeholder="Select Rate Type"
                options={RATE_TYPE_OPTIONS}
                value={
                  data.rate_type
                    ? {
                        value: data.rate_type,
                        label:
                          RATE_TYPE_OPTIONS.find(
                            (opt) => opt.value === data.rate_type,
                          )?.label || data.rate_type,
                      }
                    : undefined
                }
                onChange={(option) => onUpdate({ rate_type: option?.value })}
              />
            </div>
            {hasRateTypeError && (
              <div className={css(styles.errorMessage)}>
                Please select a rate type.
              </div>
            )}
          </div>
          <div className={css(styles.formGroup, styles.halfWidth)}>
            <label className={css(styles.label)}>Pay Rate *</label>
            <div className={css(hasPayRateError && styles.errorHighlight)}>
              <CurrencyInput
                value={data.pay_rate}
                onChange={(value) => onUpdate({ pay_rate: value })}
                disabled={readOnly}
              />
            </div>
            {hasPayRateError && (
              <div className={css(styles.errorMessage)}>
                Please enter a pay rate greater than 0.
              </div>
            )}
          </div>
        </div>
      )}

      {selectedPaymentType === 'MILESTONE' && (
        <>
          {renderScheduleForm('Add Milestone')}
          {hasScheduleError && (
            <div className={css(styles.errorMessage)}>
              Please add at least one milestone with a pay amount greater than
              0.
            </div>
          )}
        </>
      )}

      {selectedPaymentType === 'PROGRESS' && (
        <>
          {renderScheduleForm('Add Progress Payment')}
          {hasScheduleError && (
            <div className={css(styles.errorMessage)}>
              Please add at least one progress payment with an amount greater
              than 0.
            </div>
          )}
        </>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  requiredNote: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  errorHighlight: {
    borderRadius: '6px',
    boxShadow: '0 0 0 2px #FCD34D',
    backgroundColor: '#FFFBEB',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
  subTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  halfWidth: {
    flex: 1,
  },
  paymentSection: {
    marginTop: '24px',
  },
  addButton: {
    color: '#1C64F2',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  container: {
    width: '100%',
  },
  entryContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid #E5E7EB',
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  entryNumber: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  removeButton: {
    color: '#EF4444',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: '14px',
    ':hover': {
      color: '#DC2626',
    },
  },
  totalsSection: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#F3F4F6',
    borderRadius: '8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #E5E7EB',
  },
  totalLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  totalValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  grandTotal: {
    borderBottom: 'none',
    paddingTop: '12px',
    marginTop: '4px',
    borderTop: '2px solid #374151',
  },
})

export default PaymentInfoStep
